import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { Button } from '@nazka/nazka.mapframe.components';

import PanelHeader from '@App/components/PanelHeader/PanelHeader';
import PanelList, { getPropertyRecord } from '@App/components/PanelList/PanelList';
import FlowingWaterLegendControl from '@App/components/Map/controls/FlowingWaterLegendControl/FlowingWaterLegendControl';
import { useFlowingWaterSegments } from '@services/query.service';
import { getFeaturesBounds } from '@utils/geometry.utils';

import { getSourceColorExpression, layers, sources } from '../../sources.layers';
import { inactiveSourceLayers } from '../flowing-water.layers';

import '../flowing-water.css';

const flowingWaterSegmentsSourceId = 'active-flowing-water-segments';
const flowingWaterSegmentsLayerId = 'active-flowing-water-segments-line';
const flowingWaterSegmentsHitAreaLayerId = 'active-flowing-water-segments-hit-area';
const selectedFlowingWaterSegmentSourceId = 'selected-flowing-water-segment';
const selectedFlowingWaterSegmentBorderLayerId = 'selected-flowing-water-segment-border';
const selectedFlowingWaterSegmentLayerId = 'selected-flowing-water-segment-line';
const hoveredFlowingWaterSegmentSourceId = 'hovered-flowing-water-segment';
const hoveredFlowingWaterSegmentBorderLayerId = 'hovered-flowing-water-segment-border';
const hoveredFlowingWaterSegmentLayerId = 'hovered-flowing-water-segment-line';
function getSegmentProperties(segment) {
  if (segment.properties) return segment.properties;

  return Object.fromEntries(
    Object.entries(segment).filter(([key]) => key !== 'geometry'),
  );
}

function getSegmentMaximums(segments) {
  const maximums = {
    'potential': 0,
    'temperature': 0,
    'heat-delivered': 0,
  };
  const propertiesList = segments.map(getSegmentProperties);

  propertiesList.forEach((properties) => {
    maximums.potential = Math.max(
      maximums.potential,
      Number(properties.Bronpotentieel_jaar_bronsleutel6) / 1000000,
    );
    maximums.temperature = Math.max(
      maximums.temperature,
      Math.abs(Number(properties.DeltaTavg_bronsleutel6)),
    );
    maximums['heat-delivered'] = Math.max(
      maximums['heat-delivered'],
      Number(properties.totale_warmte_geleverd_combosleutel_bs6_vs1) / 1000000,
    );
  });

  return maximums;
}

const numberFormatter = new Intl.NumberFormat('nl-BE', {
  maximumFractionDigits: 2,
});

function formatEnergyPerYear(value) {
  if (value === undefined || value === null || value === '') return '-';

  const numericValue = Number(value);
  const absoluteValue = Math.abs(numericValue);
  if (absoluteValue >= 1_000_000) {
    return `${numberFormatter.format(numericValue / 1_000_000)} GWh/jaar`;
  }
  if (absoluteValue >= 1_000) {
    return `${numberFormatter.format(numericValue / 1_000)} MWh/jaar`;
  }

  return `${numberFormatter.format(numericValue)} kWh/jaar`;
}

function formatTemperature(value) {
  if (value === undefined || value === null || value === '') return '-';

  return `${numberFormatter.format(Number(value))} °C`;
}

function getSegmentDetails(record) {
  const getValue = (...keys) => {
    const key = keys.find(candidate => record[candidate] !== undefined);
    return key ? record[key] : undefined;
  };

  return [
    ['Rivier', record.naam_rivier],
    [
      'Totaal bronpotentieel',
      formatEnergyPerYear(record.Bronpotentieel_jaar_bronsleutel6),
    ],
    [
      'Resterend bronpotentieel',
      formatEnergyPerYear(record.Rest_bronpotentieel_jaar_bronsleutel6),
    ],
    [
      'Minimaal piekpotentieel',
      formatEnergyPerYear(getValue(
        'Bronpotentieel_minpiek_bronlsleutel6',
        'Bronpotentieel_minpiek_bronsleutel6',
      )),
    ],
    [
      'Maximaal piekpotentieel',
      formatEnergyPerYear(getValue(
        'Bronpotentiel_maxpiek_bronsleutel6',
        'Bronpotentieel_maxpiek_bronsleutel6',
      )),
    ],
    [
      'Gemiddelde delta T',
      formatTemperature(record.DeltaTavg_bronsleutel6),
    ],
    [
      'Warmte geleverd',
      formatEnergyPerYear(record.totale_warmte_geleverd_combosleutel_bs6_vs1),
    ],
  ];
}

function FlowingWater100m() {
  const { map } = useOutletContext();
  const { stromendWaterId, stromendWaterStukje100mId: highlightedSegmentId } = useParams();
  const navigate = useNavigate();
  const legendMode = useSelector(state => state.sourceLegend.selectedMode);
  const { data: segments } = useFlowingWaterSegments(stromendWaterId);
  const segmentFeatures = useMemo(
    () => (Array.isArray(segments) ? segments : segments?.features || []),
    [segments],
  );
  const segmentMaximums = useMemo(
    () => getSegmentMaximums(segmentFeatures),
    [segmentFeatures],
  );
  const panelData = segmentFeatures.find(segment => String(
    segment.id ?? segment.properties?.ID_100m ?? segment.ID_100m,
  ) === String(highlightedSegmentId));
  const panelRecord = getPropertyRecord(panelData);
  const segmentName = panelRecord.ID_100m ?? highlightedSegmentId;
  const segmentDetails = getSegmentDetails(panelRecord);

  useEffect(() => {
    if (!map || !segments) return undefined;

    const featureCollection = {
      type: 'FeatureCollection',
      features: segmentFeatures.map(segment => segment.type === 'Feature'
        ? segment
        : {
            type: 'Feature',
            geometry: segment.geometry,
            properties: getSegmentProperties(segment),
          }),
    };

    if (map.getLayer(flowingWaterSegmentsLayerId)) {
      map.removeLayer(flowingWaterSegmentsLayerId);
    }
    if (map.getSource(flowingWaterSegmentsSourceId)) {
      map.removeSource(flowingWaterSegmentsSourceId);
    }

    map.addSource(flowingWaterSegmentsSourceId, {
      type: 'geojson',
      data: featureCollection,
      promoteId: 'ID_100m',
    });
    const styleLayers = map.getStyle().layers;
    const roadLabelsIndex = styleLayers.findIndex(layer => layer.id === 'Road labels');
    const layerAfterRoadLabelsId = roadLabelsIndex >= 0
      ? styleLayers[roadLabelsIndex + 1]?.id
      : undefined;

    map.addLayer({
      id: flowingWaterSegmentsLayerId,
      type: 'line',
      source: flowingWaterSegmentsSourceId,
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': getSourceColorExpression(
          legendMode,
          'flowingWater',
          false,
          segmentMaximums[legendMode],
        ),
        'line-width': 4,
        'line-opacity': 0.9,
      },
    }, layerAfterRoadLabelsId);
    map.addLayer({
      id: flowingWaterSegmentsHitAreaLayerId,
      type: 'line',
      source: flowingWaterSegmentsSourceId,
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#000',
        'line-width': 16,
        'line-opacity': 0.01,
      },
    }, layerAfterRoadLabelsId);
    map.addSource(selectedFlowingWaterSegmentSourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
    map.addLayer({
      id: selectedFlowingWaterSegmentBorderLayerId,
      type: 'line',
      source: selectedFlowingWaterSegmentSourceId,
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#fff',
        'line-width': 18,
        'line-opacity': 1,
      },
    }, layerAfterRoadLabelsId);
    map.addLayer({
      id: selectedFlowingWaterSegmentLayerId,
      type: 'line',
      source: selectedFlowingWaterSegmentSourceId,
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': getSourceColorExpression(
          legendMode,
          'flowingWater',
          false,
          segmentMaximums[legendMode],
        ),
        'line-width': 12,
        'line-opacity': 0.9,
      },
    }, layerAfterRoadLabelsId);
    map.addSource(hoveredFlowingWaterSegmentSourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
    map.addLayer({
      id: hoveredFlowingWaterSegmentBorderLayerId,
      type: 'line',
      source: hoveredFlowingWaterSegmentSourceId,
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#fff',
        'line-width': 14,
        'line-opacity': 1,
      },
    }, layerAfterRoadLabelsId);
    map.addLayer({
      id: hoveredFlowingWaterSegmentLayerId,
      type: 'line',
      source: hoveredFlowingWaterSegmentSourceId,
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': getSourceColorExpression(
          legendMode,
          'flowingWater',
          false,
          segmentMaximums[legendMode],
        ),
        'line-width': 10,
        'line-opacity': 0.9,
      },
    }, layerAfterRoadLabelsId);
    map.moveLayer(
      hoveredFlowingWaterSegmentBorderLayerId,
      selectedFlowingWaterSegmentBorderLayerId,
    );
    map.moveLayer(
      hoveredFlowingWaterSegmentLayerId,
      selectedFlowingWaterSegmentBorderLayerId,
    );
    map.moveLayer(flowingWaterSegmentsHitAreaLayerId, layerAfterRoadLabelsId);
    const highlightedFeature = highlightedSegmentId === undefined
      ? undefined
      : featureCollection.features.find(feature => String(
          feature.id ?? feature.properties?.ID_100m,
        ) === String(highlightedSegmentId));
    if (highlightedFeature) {
      map.getSource(selectedFlowingWaterSegmentSourceId).setData({
        type: 'FeatureCollection',
        features: [highlightedFeature],
      });
    }

    let hoveredFeatureId;
    const handleMouseMove = (event) => {
      const feature = event.features?.[0];
      const featureId = feature?.id;
      if (featureId === undefined || featureId === null) return;
      if (String(featureId) === String(highlightedSegmentId)) {
        handleMouseLeave();
        return;
      }

      if (hoveredFeatureId !== undefined && hoveredFeatureId !== featureId) {
        map.setFeatureState(
          { source: flowingWaterSegmentsSourceId, id: hoveredFeatureId },
          { hover: false },
        );
      }

      hoveredFeatureId = featureId;
      map.getSource(hoveredFlowingWaterSegmentSourceId).setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: feature.geometry,
          properties: feature.properties || {},
        }],
      });
      map.setFeatureState(
        { source: flowingWaterSegmentsSourceId, id: featureId },
        { hover: true },
      );
      map.getCanvas().style.cursor = 'pointer';
    };
    const handleMouseLeave = () => {
      if (hoveredFeatureId !== undefined) {
        map.setFeatureState(
          { source: flowingWaterSegmentsSourceId, id: hoveredFeatureId },
          { hover: false },
        );
        hoveredFeatureId = undefined;
      }
      map.getSource(hoveredFlowingWaterSegmentSourceId)?.setData({
        type: 'FeatureCollection',
        features: [],
      });
      map.getCanvas().style.cursor = '';
    };
    const handleSegmentClick = (event) => {
      const featureId = event.features?.[0]?.id;
      if (featureId !== undefined && featureId !== null) {
        navigate(`/bronnen/stromend-water/${stromendWaterId}/${featureId}`);
      }
    };
    map.on('mousemove', flowingWaterSegmentsHitAreaLayerId, handleMouseMove);
    map.on('mouseleave', flowingWaterSegmentsHitAreaLayerId, handleMouseLeave);
    map.on('click', flowingWaterSegmentsHitAreaLayerId, handleSegmentClick);

    return () => {
      map.off('mousemove', flowingWaterSegmentsHitAreaLayerId, handleMouseMove);
      map.off('mouseleave', flowingWaterSegmentsHitAreaLayerId, handleMouseLeave);
      map.off('click', flowingWaterSegmentsHitAreaLayerId, handleSegmentClick);
      handleMouseLeave();
      if (map.getLayer(flowingWaterSegmentsHitAreaLayerId)) {
        map.removeLayer(flowingWaterSegmentsHitAreaLayerId);
      }
      if (map.getLayer(hoveredFlowingWaterSegmentLayerId)) {
        map.removeLayer(hoveredFlowingWaterSegmentLayerId);
      }
      if (map.getLayer(hoveredFlowingWaterSegmentBorderLayerId)) {
        map.removeLayer(hoveredFlowingWaterSegmentBorderLayerId);
      }
      if (map.getLayer(selectedFlowingWaterSegmentLayerId)) {
        map.removeLayer(selectedFlowingWaterSegmentLayerId);
      }
      if (map.getLayer(selectedFlowingWaterSegmentBorderLayerId)) {
        map.removeLayer(selectedFlowingWaterSegmentBorderLayerId);
      }
      if (map.getLayer(flowingWaterSegmentsLayerId)) {
        map.removeLayer(flowingWaterSegmentsLayerId);
      }
      if (map.getSource(hoveredFlowingWaterSegmentSourceId)) {
        map.removeSource(hoveredFlowingWaterSegmentSourceId);
      }
      if (map.getSource(selectedFlowingWaterSegmentSourceId)) {
        map.removeSource(selectedFlowingWaterSegmentSourceId);
      }
      if (map.getSource(flowingWaterSegmentsSourceId)) {
        map.removeSource(flowingWaterSegmentsSourceId);
      }
    };
  }, [
    highlightedSegmentId,
    legendMode,
    map,
    navigate,
    segmentFeatures,
    segmentMaximums,
    segments,
    stromendWaterId,
  ]);

  useEffect(() => {
    if (!map) return undefined;

    const legendControl = new FlowingWaterLegendControl(segmentMaximums);
    map.addControl(legendControl, 'bottom-right');

    return () => {
      map.removeControl(legendControl);
    };
  }, [map, segmentMaximums]);

  useEffect(() => {
    if (!map || !panelData?.geometry) return undefined;

    const featureBounds = getFeaturesBounds([panelData]);
    if (!featureBounds) return undefined;

    let secondFrame;
    const fitFeature = () => {
      map.resize();

      const [[minLongitude, minLatitude], [maxLongitude, maxLatitude]] = featureBounds;
      if (minLongitude === maxLongitude && minLatitude === maxLatitude) {
        map.flyTo({
          center: [minLongitude, minLatitude],
          zoom: 14,
          duration: 1000,
        });
        return;
      }

      map.fitBounds(featureBounds, {
        padding: {
          top: 48,
          right: 128,
          bottom: 48,
          left: 450,
        },
        maxZoom: 18,
        duration: 1000,
      });
    };
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(fitFeature);
    });

    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame) cancelAnimationFrame(secondFrame);
    };
  }, [map, panelData]);

  useEffect(() => {
    if (!map) return undefined;

    const s3BucketUrl = import.meta.env.VITE_S3_BUCKET_URL;
    if (!s3BucketUrl) return undefined;

    const sourceEntries = Object.values(sources);
    const layerEntries = Object.values(layers);

    sourceEntries.forEach((source) => {
      if (!map.getSource(source.id)) {
        map.addSource(source.id, {
          type: source.type,
          url: `pmtiles://${s3BucketUrl}/${source.pmtilesFile}`,
          promoteId: source.promoteId,
        });
      }
    });

    const styleLayers = map.getStyle().layers;
    const roadLabelsIndex = styleLayers.findIndex(layer => layer.id === 'Road labels');
    const layerAfterRoadLabelsId = roadLabelsIndex >= 0
      ? styleLayers[roadLabelsIndex + 1]?.id
      : undefined;

    layerEntries.forEach((layer) => {
      if (!map.getLayer(layer.id)) map.addLayer(layer, layerAfterRoadLabelsId);
    });

    const activeWaterId = Number(stromendWaterId);
    if (Number.isFinite(activeWaterId)) {
      const activeWaterFilter = [
        '!=',
        ['get', 'ID_stromend'],
        activeWaterId,
      ];
      map.setFilter(layers.flowingWater.id, activeWaterFilter);
      map.setFilter(layers.flowingWaterHitArea.id, activeWaterFilter);
    }

    Object.entries(inactiveSourceLayers).forEach(([layerName, paintProperties]) => {
      Object.entries(paintProperties).forEach(([property, value]) => {
        map.setPaintProperty(layers[layerName].id, property, value);
      });
    });

    const navigationTargets = {
      [layers.flowingWaterHitArea.id]: feature => `/bronnen/stromend-water/${feature.id}`,
      [layers.stillWater.id]: feature => `/bronnen/stilstaand-water/${feature.id}`,
      [layers.stillWaterOutline.id]: feature => `/bronnen/stilstaand-water/${feature.id}`,
      [layers.rwzi.id]: feature => `/bronnen/rwzi/${feature.id}`,
    };
    const clickHandlers = Object.entries(navigationTargets).map(([layerId, getPath]) => {
      const handler = (event) => {
        const feature = event.features?.[0];
        if (feature?.id !== undefined && feature?.id !== null) {
          navigate(getPath(feature));
        }
      };
      map.on('click', layerId, handler);
      return { layerId, handler };
    });

    const interactiveLayerIds = [
      ...Object.keys(navigationTargets),
      flowingWaterSegmentsHitAreaLayerId,
    ];
    let hoveredFeature;
    const clearHoveredFeature = () => {
      if (!hoveredFeature) return;
      map.setFeatureState(hoveredFeature, { hover: false });
      hoveredFeature = undefined;
    };
    const handleMouseMove = (event) => {
      const features = map.queryRenderedFeatures(event.point, {
        layers: interactiveLayerIds,
      });
      const feature = features[0];
      if (!feature) {
        clearHoveredFeature();
        map.getCanvas().style.cursor = '';
        return;
      }
      if (
        feature.source === flowingWaterSegmentsSourceId
        && String(feature.id) === String(highlightedSegmentId)
      ) {
        clearHoveredFeature();
        map.getCanvas().style.cursor = '';
        return;
      }

      const featureStateTarget = {
        source: feature.source,
        sourceLayer: feature.sourceLayer,
        id: feature.id,
      };
      if (
        featureStateTarget.id !== undefined
        && featureStateTarget.id !== null
        && (
          !hoveredFeature
          || hoveredFeature.id !== featureStateTarget.id
          || hoveredFeature.source !== featureStateTarget.source
        )
      ) {
        clearHoveredFeature();
        hoveredFeature = featureStateTarget;
        map.setFeatureState(hoveredFeature, { hover: true });
      }
      map.getCanvas().style.cursor = features.length > 0 ? 'pointer' : '';
    };
    map.on('mousemove', handleMouseMove);

    return () => {
      clickHandlers.forEach(({ layerId, handler }) => map.off('click', layerId, handler));
      map.off('mousemove', handleMouseMove);
      clearHoveredFeature();
      map.getCanvas().style.cursor = '';
      if (map.getLayer(layers.flowingWater.id)) {
        map.setFilter(layers.flowingWater.id, null);
      }
      if (map.getLayer(layers.flowingWaterHitArea.id)) {
        map.setFilter(layers.flowingWaterHitArea.id, null);
      }
      [...layerEntries].reverse().forEach(({ id }) => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
      [...sourceEntries].reverse().forEach(({ id }) => {
        if (map.getSource(id)) map.removeSource(id);
      });
    };
  }, [highlightedSegmentId, map, navigate, stromendWaterId]);

  return (
    <div className="flowing-water">
      <PanelHeader
        map={map}
        rootPath="/bronnen"
        parentPath={`/bronnen/stromend-water/${stromendWaterId}`}
        parentName={panelRecord.naam_rivier}
        currentName={segmentName}
      />
      <PanelList details={segmentDetails} />
      <Button className="flowing-water__simulation-button">
        Ik wil hier een project simuleren
      </Button>
    </div>
  );
}

export default FlowingWater100m;
